import { ProductionRecipeVariationFieldsStatus } from "~/gql/graphqlBase.generated";
import {
	ActivateProductionRecipeMutation,
	AddProductionRecipeVariationMutation,
	BatchUpdateProductionRecipeVariationFieldsMutation,
	BatchUpdateProductionRecipeVariationsMutation,
	CreateProductionRecipeMutation,
	CreateProductionRecipeWithVariationMutation,
	DeleteProductionRecipeMutation,
	DuplicateProductionRecipeMutation,
	InactivateProductionRecipeMutation,
	RemoveProductionRecipeVariationMutation,
	RepointProductionRecipeLinksMutation,
	SetProductionRecipeAdditionalCostEventIdsMutation,
	SetProductionRecipeFlatRateCostMutation,
	SetProductionRecipeMaterialVendorMutation,
	SetProductionRecipeTaskEventIdsMutation,
	SetProductionRecipeTypeMutation,
	SetRecipeLaborSourceMutation,
	UpdateProductionRecipeVariationFieldsMutation,
	UpdateProductionRecipeVariationMutation
} from "~/gql/mutations/produce/productionRecipes.generated";
import {
	GetProductionRecipeBomQuery,
	GetProductionRecipeChainQuery,
	GetProductionRecipeHistoryQuery,
	GetProductionRecipeMaterialVendorOverridesQuery,
	GetProductionRecipeQuery,
	GetProductionRecipesLinkingPairQuery,
	GetProductionRecipesQuery,
	GetProductionRecipeVariationEffectiveYieldQuery,
	GetProductionRecipeVariationsQuery,
	GetRecipeVariationTagsQuery
} from "~/gql/queries/produce/productionRecipes.generated";

export const productionRecipeEndpoints = {
	ActivateProductionRecipe: {
		invalidatesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: ActivateProductionRecipeMutation) => res
	},
	AddProductionRecipeVariation: {
		invalidatesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: AddProductionRecipeVariationMutation) => res
	},
	BatchUpdateProductionRecipeVariationFields: {
		invalidatesTags: (res: BatchUpdateProductionRecipeVariationFieldsMutation | undefined) =>
			res?.batchUpdateProductionRecipeVariationFields.results.some(
				(result) => result.status === ProductionRecipeVariationFieldsStatus.Applied
			)
				? ["ProductionRecipe" as const]
				: [],
		transformResponse: (res: BatchUpdateProductionRecipeVariationFieldsMutation) => res
	},
	BatchUpdateProductionRecipeVariations: {
		invalidatesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: BatchUpdateProductionRecipeVariationsMutation) => res
	},
	CreateProductionRecipe: {
		invalidatesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: CreateProductionRecipeMutation) => res
	},
	CreateProductionRecipeWithVariation: {
		invalidatesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: CreateProductionRecipeWithVariationMutation) => res
	},
	DeleteProductionRecipe: {
		invalidatesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: DeleteProductionRecipeMutation) => res
	},
	DuplicateProductionRecipe: {
		invalidatesTags: (res: DuplicateProductionRecipeMutation | undefined) =>
			res?.duplicateProductionRecipe.productionRecipe ? ["ProductionRecipe" as const] : [],
		transformResponse: (res: DuplicateProductionRecipeMutation) => res
	},
	GetProductionRecipe: {
		providesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: GetProductionRecipeQuery) => res
	},
	GetProductionRecipeBom: {
		providesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: GetProductionRecipeBomQuery) => res
	},
	GetProductionRecipeChain: {
		providesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: GetProductionRecipeChainQuery) => res
	},
	GetProductionRecipeHistory: {
		providesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: GetProductionRecipeHistoryQuery) => res
	},
	GetProductionRecipeMaterialVendorOverrides: {
		providesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: GetProductionRecipeMaterialVendorOverridesQuery) => res
	},
	GetProductionRecipes: {
		providesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: GetProductionRecipesQuery) => res
	},
	GetProductionRecipesLinkingPair: {
		providesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: GetProductionRecipesLinkingPairQuery) => res
	},
	GetProductionRecipeVariationEffectiveYield: {
		providesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: GetProductionRecipeVariationEffectiveYieldQuery) => res
	},
	GetProductionRecipeVariations: {
		providesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: GetProductionRecipeVariationsQuery) => res
	},
	GetRecipeVariationTags: {
		providesTags: ["ProductionRecipe", "ProduceTags"] as AddTagTypes,
		transformResponse: (res: GetRecipeVariationTagsQuery) => res
	},
	InactivateProductionRecipe: {
		invalidatesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: InactivateProductionRecipeMutation) => res
	},
	RemoveProductionRecipeVariation: {
		invalidatesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: RemoveProductionRecipeVariationMutation) => res
	},
	RepointProductionRecipeLinks: {
		invalidatesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: RepointProductionRecipeLinksMutation) => res
	},
	SetProductionRecipeAdditionalCostEventIds: {
		invalidatesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: SetProductionRecipeAdditionalCostEventIdsMutation) => res
	},
	SetProductionRecipeFlatRateCost: {
		invalidatesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: SetProductionRecipeFlatRateCostMutation) => res
	},
	SetProductionRecipeMaterialVendor: {
		invalidatesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: SetProductionRecipeMaterialVendorMutation) => res
	},
	SetProductionRecipeTaskEventIds: {
		invalidatesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: SetProductionRecipeTaskEventIdsMutation) => res
	},
	SetProductionRecipeType: {
		invalidatesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: SetProductionRecipeTypeMutation) => res
	},
	SetRecipeLaborSource: {
		invalidatesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: SetRecipeLaborSourceMutation) => res
	},
	UpdateProductionRecipeVariation: {
		invalidatesTags: ["ProductionRecipe"] as AddTagTypes,
		transformResponse: (res: UpdateProductionRecipeVariationMutation) => res
	},
	UpdateProductionRecipeVariationFields: {
		invalidatesTags: (res: UpdateProductionRecipeVariationFieldsMutation | undefined) =>
			res?.updateProductionRecipeVariationFields.status === ProductionRecipeVariationFieldsStatus.Applied
				? ["ProductionRecipe" as const]
				: [],
		transformResponse: (res: UpdateProductionRecipeVariationFieldsMutation) => res
	}
};
